import type { Component } from "@/types";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useViewerStore } from "@/stores/viewer/viewer-store";
import type { WritebackConfig } from "@/types/pm-types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const hasNonStaticDataSource = (component: Component): boolean => {
  const dataSource = component.properties.dataSource;
  if (!isRecord(dataSource)) return false;

  const type = dataSource.type;
  return typeof type === "string" && type.length > 0 && type !== "static";
};

const hasQueryTemplateBinding = (component: Component): boolean =>
  typeof component.properties.queryTemplateId === "string" &&
  component.properties.queryTemplateId.trim().length > 0;

export const canMutateLocalTableRows = (component: Component): boolean =>
  (component.properties.dataBinding === undefined ||
    component.properties.dataBinding === null) &&
  !hasQueryTemplateBinding(component) &&
  !hasNonStaticDataSource(component);

const getTrimmedString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

export const getTableRowWritebackConfig = (
  component: Component,
): WritebackConfig | undefined => {
  const dataSource = component.properties.dataSource;
  if (!isRecord(dataSource) || dataSource.type !== "query-template") {
    return undefined;
  }

  const queryDefinition = dataSource.queryDefinition;
  if (!isRecord(queryDefinition)) {
    return undefined;
  }

  const groupId = getTrimmedString(queryDefinition.groupId);
  const schemaId = getTrimmedString(queryDefinition.schemaId);
  const dataOwnerOrganizationId = getTrimmedString(queryDefinition.organizationId);
  if (
    groupId === undefined ||
    schemaId === undefined ||
    dataOwnerOrganizationId === undefined
  ) {
    return undefined;
  }

  return {
    enabled: true,
    dataOwnerOrganizationId,
    groupId,
    schemaId,
    idField: "_id",
  };
};

export const getTableRowRecordId = (
  row: Record<string, unknown>,
): string | undefined => {
  const directIdKeys = [
    "_id",
    "id",
    "recordId",
    "record_id",
    "documentId",
    "document_id",
  ];
  for (const key of directIdKeys) {
    const id = getTrimmedString(row[key]);
    if (id !== undefined) return id;
  }

  const nestedIdContainers = [row.metadata, row.data];
  for (const container of nestedIdContainers) {
    if (!isRecord(container)) continue;
    for (const key of directIdKeys) {
      const id = getTrimmedString(container[key]);
      if (id !== undefined) return id;
    }
  }

  return undefined;
};

export const generateLocalTableRowId = (): string => {
  const randomSuffix = Math.random().toString(36).slice(2, 9);
  return `row-${Date.now()}-${randomSuffix}`;
};

export const commitLocalTableRows = (
  component: Component,
  rows: Record<string, unknown>[],
): boolean => {
  const editorState = useEditorStore.getState();
  const editorComponent = editorState.getComponent?.(component.id);
  if (editorComponent) {
    editorState.updateComponent(component.id, {
      properties: {
        ...editorComponent.properties,
        data: rows,
      },
    });
    return true;
  }

  const viewerState = useViewerStore.getState();
  const viewerComponent = viewerState.getComponentById?.(component.id);
  if (viewerComponent) {
    viewerState.updateComponent(component.id, {
      properties: {
        ...viewerComponent.properties,
        data: rows,
      },
    });
    return true;
  }

  return false;
};
